/**
 * 포트폴리오 생성 & 처방 계산 추적 훅
 * 
 * 포트폴리오 티저 계산(1차 계산)의 의도-결과를 추적
 * - portfolio_create_compute_start: 계산 시작
 * - portfolio_create_compute_success: 계산 성공
 * - portfolio_create_compute_fail: 계산 실패
 * 
 * - portfolio_diagnosis_compute_start: 계산 시작
 * - portfolio_diagnosis_compute_success: 계산 성공
 * - portfolio_diagnosis_compute_fail: 계산 실패
 * 
 * @example
 * ```tsx
 * function PortfolioTeaserPage() {
 *   const createCompute = usePortfolioCompute({ type: 'create' });
 *   
 *   const handleComputeTeaser = async () => {
 *     createCompute.start(); // portfolio_id 없이 시작
 *     
 *     try {
 *       const result = await computeTeaserAPI();
 *       createCompute.success({ portfolio_id: result.id }); // success 시 전달
 *     } catch (error) {
 *       createCompute.fail('backend_5xx');
 *     }
 *   };
 * }
 * ```
 */

import { useRef } from 'react';
import { useIntent } from '../useIntent';
import { track } from '../../../../core/client';

export interface PortfolioComputeContext {
    portfolio_id?: number;
    tickers?: any[];
}

export interface PortfolioComputeActions {
    start: (context?: PortfolioComputeContext, options?: {
        parentIntentId?: string;
        parentIntentType?: string;
    }) => string;
    success: (context: PortfolioComputeContext) => void;
    fail: (reason?: string) => void;
}

/**
 * 포트폴리오 생성 계산 추적 훅
 */
export function usePortfolioCompute({ type }: { type: 'create' | 'diagnosis' }): PortfolioComputeActions {
    // success 시 전달받을 context를 저장하는 ref
    const successContextRef = useRef<PortfolioComputeContext | null>(null);

    const typeMap = {
        create: {
            start: 'portfolio_create_compute_start',
            success: 'portfolio_create_compute_success',
            fail: 'portfolio_create_compute_fail',
        },
        diagnosis: {
            start: 'portfolio_diagnosis_compute_start',
            success: 'portfolio_diagnosis_compute_success',
            fail: 'portfolio_diagnosis_compute_fail',
        }
    }

    const intent = useIntent<PortfolioComputeContext>({
        type: typeMap[type].start,
        requiredGate: 'none',
        onStart: ({ intent_id, context, parent_intent_id, required_gate }) => {
            track(typeMap[type].start as any, {
                intent_id,
                portfolio_id: context?.portfolio_id || undefined,
                parent_intent_id,
                required_gate,
                tickers: context?.tickers || undefined,
            });
            // console.log(`🎯 [PortfolioCompute:${type}] Start [intent_id: ${intent_id}${context?.portfolio_id ? `, portfolio_id: ${context.portfolio_id}` : ''}]`);
        },
        onSuccess: ({ intent_id, latency_ms }) => {
            const successContext = successContextRef.current;
            track(typeMap[type].success as any, {
                intent_id,
                portfolio_id: successContext?.portfolio_id,
                latency_ms,
            });
            // console.log(`✅ [PortfolioCompute:${type}] Success [intent_id: ${intent_id}, portfolio_id: ${successContext?.portfolio_id}]`);
        },
        onFail: ({ intent_id, context, fail_reason }) => {
            track(typeMap[type].fail as any, {
                intent_id,
                portfolio_id: context?.portfolio_id,
                fail_reason,
            });
            // console.log(`❌ [PortfolioCompute:${type}] Fail [intent_id: ${intent_id}${context?.portfolio_id ? `, portfolio_id: ${context.portfolio_id}` : ''}, reason: ${fail_reason}]`);
        },
    });

    return {
        start: (context, options) => intent.start(context, options),
        success: (context: PortfolioComputeContext) => {
            successContextRef.current = context;
            intent.success();
        },
        fail: (reason) => intent.fail(reason),
    };
}

